import { lazy, Suspense, useState } from 'react';
import { useHydrated } from 'remix-utils';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Download } from 'lucide-react';
import { Button } from '@rocco/components/button';
import Description from '@modules/product/component/product-description/Description';
import { useTrackProductView } from '@modules/product/hook/use-track-product-view';
import VariantPOAMobile from '../variant-pricing/VariantPOA.mobile';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import CompanyProfile from '~/modules/company-profile/CompanyProfile';
import { AwardAccordion } from './AwardAccordion';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';
import ProductDetailsCTA from './ProductDetailsCTA';
import { useProductFilesModalDeepLink } from '@modules/product/hook/use-product-files-modal-deep-link';
import { useProductFilesModalReopen } from '@modules/product/hook/use-product-files-modal-reopen';

const ProductFilesModal = lazy(
    () => import('@modules/product/component/downloads/ProductFilesModal'),
);

export interface ProductCategory {
    PrettyTitle: string;
    Link: string;
    Parents?: ProductCategory[];
}

interface ProductDetailsProps {
    data: ProductPageLoaderData['product'] & {
        primaryCTA: ProductPageLoaderData['primaryCTA'];
        primaryCTASectionRef: React.RefObject<HTMLDivElement | null>;
        enquireSectionRef: React.RefObject<HTMLDivElement | null>;
        cartRegion?: {
            ID: number;
            Title: string;
        } | null;
    };
    /** Branch options for the enquiry modal dropdown. */
    branchOptions: ProductPageLoaderData['branchOptions'];
    whereToBuyEnabled?: boolean;
    onClickWhereToBuy?: () => void;
    /** Callback to open the enquiry modal on mobile. */
    onEnquire?: () => void;
    /** Whether to show the description section. Defaults to true. */
    showDescription?: boolean;
}

const ProductDetails = ({
    data,
    branchOptions,
    whereToBuyEnabled,
    onClickWhereToBuy,
    onEnquire,
    showDescription = true,
}: ProductDetailsProps) => {
    const { mobile } = useAppDisplayModeContext();
    const hydrated = useHydrated();

    // Determine enquiry visibility based on professional settings and user type
    const { showEnquiryCTA, showEnquiryForm, showSpecifierLoginPrompt } =
        useEnquiryVisibility(data.Professional);

    useTrackProductView(data);

    const productID = data.ID;

    const validLibraryDownloads = data.LibraryDownloads.filter(
        (d) => d.InternalLink || d.ExternalLink,
    );
    const hasDownloads = validLibraryDownloads.length > 0;

    const [productFilesModalOpen, setProductFilesModalOpen] = useState(false);

    useProductFilesModalReopen({
        productID,
        professionalID: data.Professional.ID,
        enabled: mobile && hasDownloads,
        onReopen: () => setProductFilesModalOpen(true),
    });

    // Mobile owns the files popup (desktop uses ProductDetailsCTA). Same
    // `#product-files` CMS link should open it here.
    useProductFilesModalDeepLink({
        enabled: mobile && hasDownloads,
        open: productFilesModalOpen,
        onOpen: () => setProductFilesModalOpen(true),
    });

    return (
        <div className="md:pt-6">
            {!data.IsInServiceableArea && (
                <div className="mt-4 text-base leading-none md:mt-0 md:mb-4 md:text-4.5">
                    Not available in{' '}
                    {data.cartRegion?.Title || 'your location.'}
                </div>
            )}
            {showDescription && <Description product={data} />}
            {data.Awards.length ? (
                <AwardAccordion awards={data.Awards} />
            ) : null}
            {mobile && showEnquiryCTA && (
                <div ref={data.primaryCTASectionRef}>
                    <VariantPOAMobile
                        professional={{
                            title: data.Professional.Title,
                            id: data.Professional.ID,
                        }}
                        itemID={data.ID}
                        primaryCTA={data.primaryCTA}
                        isInServiceableArea={data.IsInServiceableArea}
                        whereToBuyEnabled={whereToBuyEnabled}
                        onClickWhereToBuy={onClickWhereToBuy}
                        onEnquire={onEnquire}
                        product={data}
                    />
                </div>
            )}
            {data.IsInServiceableArea && (
                <div ref={data.enquireSectionRef}>
                    {/* Mobile: Show Visit Website CTA and professional info */}
                    {mobile && showEnquiryForm && (
                        <CompanyProfile
                            data={{
                                Professional: data.Professional,
                                siteTreeID: productID,
                                primaryCTA: data.primaryCTA,
                                website:
                                    data.Website || data.Professional.Website,
                                itemType: 'product',
                            }}
                            showCTABottom
                            showViewProfile={false}
                            showEnquiryForm={false}
                            removeTopSpacing
                            showWhereToBy={whereToBuyEnabled}
                            onClickWhereToBuy={onClickWhereToBuy}
                        />
                    )}
                    {/* Desktop: Show CTA buttons matching page header style */}
                    {!mobile && (
                        <ProductDetailsCTA
                            pageID={productID}
                            productTitle={data.Title}
                            website={data.Website || data.Professional.Website}
                            professionalID={data.Professional.ID}
                            professionalTitle={data.Professional.Title}
                            primaryCTALabel={
                                data.primaryCTA.primaryCTALabelTitleCase
                            }
                            branchOptions={branchOptions}
                            companyLogo={data.Professional.CompanyLogo ?? ''}
                            logoBackgroundColor={
                                data.Professional.LogoBackgroundColor ?? 'white'
                            }
                            productImage={
                                data.LibraryImages?.[0]?.LibraryImageFile
                                    ?.PhotoTile ?? undefined
                            }
                            showEnquiryCTA={showEnquiryCTA}
                            samplesAvailable={data.SamplesAvailable}
                            libraryDownloads={data.LibraryDownloads}
                            customEnquiryMessage={
                                data.Professional.CustomEnquiryMessage ??
                                undefined
                            }
                            enquiryMessageOverrideEnabled={
                                data.Professional
                                    .EnquiryMessageOverrideEnabled ?? undefined
                            }
                            enquiryMessagePlaceholder={
                                data.Professional.EnquiryMessagePlaceholder ??
                                undefined
                            }
                        />
                    )}
                </div>
            )}
            {/* Mobile: Open product files in a popup matching the desktop design */}
            {mobile && hasDownloads && (
                <>
                    <div className="border-t border-gray-200 py-4">
                        <Button
                            color="secondary"
                            weight="default"
                            size="larger"
                            className="w-full uppercase"
                            onClick={() => setProductFilesModalOpen(true)}
                        >
                            Product Files ({validLibraryDownloads.length})
                            <Download className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                    {hydrated && (
                        <Suspense fallback={null}>
                            <ProductFilesModal
                                open={productFilesModalOpen}
                                onOpenChange={setProductFilesModalOpen}
                                libraryDownloads={validLibraryDownloads}
                                productID={productID}
                                professionalID={data.Professional.ID}
                                professionalTitle={data.Professional.Title}
                                productImage={
                                    data.LibraryImages?.[0]?.LibraryImageFile
                                        ?.PhotoTile ?? undefined
                                }
                            />
                        </Suspense>
                    )}
                </>
            )}
            {showSpecifierLoginPrompt && (
                <SpecifierLoginPrompt
                    professionalName={data.Professional.Title}
                    showBottomBorder={false}
                    className="mt-6"
                />
            )}
        </div>
    );
};

export default ProductDetails;
